import React, { useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    document.title = "Privacy Policy | Exeter Flow Screeds";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        "content",
        "Privacy Policy for Exeter Flow Screeds Ltd. Learn how we collect, store, and protect your personal information under UK GDPR.",
      );

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical)
      canonical.setAttribute(
        "href",
        "https://exeterflowscreed.co.uk/privacy-policy",
      );

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl)
      ogUrl.setAttribute(
        "content",
        "https://exeterflowscreed.co.uk/privacy-policy",
      );
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#080810] text-stone-300 font-sans flex flex-col justify-between">
      <Navbar />

      <main className="pt-32 sm:pt-40 pb-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto w-full">
        <div className="mb-10 pb-8 border-b border-stone-800">
          <p className="text-[#C9A84C] text-xs font-bold tracking-widest uppercase mb-3">
            LEGAL INFORMATION
          </p>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white mb-4 tracking-tight">
            Privacy Policy
          </h1>
          <p className="text-stone-400 text-sm">Last updated: 23 August 2026</p>
        </div>

        <div className="space-y-8 text-stone-300 leading-relaxed text-sm sm:text-base">
          <section className="space-y-4">
            <p>
              This Privacy Policy describes our policies and procedures on the
              collection, use, and disclosure of your information when you use
              our website (
              <a
                href="https://exeterflowscreed.co.uk/"
                className="text-[#C9A84C] hover:underline"
              >
                exeterflowscreed.co.uk
              </a>
              ) and services, and tells you about your privacy rights and how UK
              law protects you.
            </p>
            <p>
              <strong className="text-white">Exeter Flow Screeds Ltd</strong> is
              committed to protecting your personal data in accordance with the
              UK General Data Protection Regulation (UK GDPR) and the Data
              Protection Act 2018.
            </p>
          </section>

          <section className="space-y-4 pt-4 border-t border-stone-800/80">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              1. Data Controller Details
            </h2>
            <p>
              For the purposes of the UK GDPR, Exeter Flow Screeds Ltd is the
              data controller responsible for your personal data.
            </p>
            <ul className="list-disc list-inside space-y-1 text-stone-400 pl-2">
              <li>
                <strong className="text-stone-200">Business Name:</strong>{" "}
                Exeter Flow Screeds Ltd
              </li>
              <li>
                <strong className="text-stone-200">Phone:</strong> 07765 712609
                / 07470 861869
              </li>
              <li>
                <strong className="text-stone-200">Email:</strong>{" "}
                <a
                  href="mailto:efscreed@gmail.com"
                  className="text-[#C9A84C] hover:underline"
                >
                  efscreed@gmail.com
                </a>
              </li>
              <li>
                <strong className="text-stone-200">Address:</strong> Turner Cl,
                Newton Poppleford, Sidmouth EX10 0EN
              </li>
            </ul>
          </section>

          <section className="space-y-4 pt-4 border-t border-stone-800/80">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              2. Information We Collect
            </h2>
            <p>
              While using our website or contacting us for a quote, we may ask
              you to provide us with certain personally identifiable
              information, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-stone-400 pl-2">
              <li>First and last name</li>
              <li>Phone number(s)</li>
              <li>Email address</li>
              <li>
                Site address, postcode, and site access details for quotation
                purposes
              </li>
              <li>
                Floor area dimensions, screed depth requirements, and underfloor
                heating details
              </li>
            </ul>
          </section>

          <section className="space-y-4 pt-4 border-t border-stone-800/80">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              3. How We Use Your Personal Data
            </h2>
            <p>
              We use your personal data for the following legitimate business
              purposes:
            </p>
            <ul className="list-disc list-inside space-y-2 text-stone-400 pl-2">
              <li>
                <strong className="text-stone-200">
                  Quotation & Enquiries:
                </strong>{" "}
                To calculate fixed quotes, assess site conditions, and respond
                to your requests.
              </li>
              <li>
                <strong className="text-stone-200">Service Delivery:</strong> To
                arrange site visits, coordinate liquid screed deliveries, and
                complete flooring installations.
              </li>
              <li>
                <strong className="text-stone-200">
                  Customer Communication:
                </strong>{" "}
                To send invoice details, schedule updates, or follow-up
                technical advice.
              </li>
              <li>
                <strong className="text-stone-200">Legal & Accounting:</strong>{" "}
                To maintain accurate tax and accounting records as required by
                UK law.
              </li>
            </ul>
          </section>

          <section className="space-y-4 pt-4 border-t border-stone-800/80">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              4. Sharing Your Personal Data
            </h2>
            <p>
              We respect your privacy and never sell or rent your personal
              information to third parties. We may only share necessary data in
              the following circumstances:
            </p>
            <ul className="list-disc list-inside space-y-2 text-stone-400 pl-2">
              <li>
                <strong className="text-stone-200">
                  Concrete & Material Suppliers:
                </strong>{" "}
                Delivery site address and access notes provided to ready-mix
                concrete batching plants to deliver liquid screed directly to
                your site.
              </li>
              <li>
                <strong className="text-stone-200">Legal Compliance:</strong> If
                required by law, regulation, or court order.
              </li>
            </ul>
          </section>

          <section className="space-y-4 pt-4 border-t border-stone-800/80">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              5. Retention & Security of Data
            </h2>
            <p>
              We retain customer contact and project records only for as long as
              necessary to fulfill the purposes for which they were collected,
              including for satisfying legal, accounting, or guarantee tracking
              requirements (such as our 10-year Cemfloor product guarantees).
            </p>
            <p>
              We implement appropriate technical and organisational security
              measures to protect your personal data against unauthorised
              access, alteration, or disclosure.
            </p>
          </section>

          <section className="space-y-4 pt-4 border-t border-stone-800/80">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              6. Your Rights Under UK GDPR
            </h2>
            <p>Under UK data protection law, you have rights including:</p>
            <ul className="list-disc list-inside space-y-2 text-stone-400 pl-2">
              <li>
                <strong className="text-stone-200">Right of access:</strong> Ask
                for copies of your personal data.
              </li>
              <li>
                <strong className="text-stone-200">
                  Right to rectification:
                </strong>{" "}
                Ask us to rectify inaccurate or incomplete data.
              </li>
              <li>
                <strong className="text-stone-200">Right to erasure:</strong>{" "}
                Request that we delete your personal data under certain
                conditions.
              </li>
              <li>
                <strong className="text-stone-200">
                  Right to restriction/object:
                </strong>{" "}
                Object to or restrict the processing of your personal data.
              </li>
            </ul>
            <p className="pt-2">
              To exercise any of these rights, please email us at{" "}
              <a
                href="mailto:efscreed@gmail.com"
                className="text-[#C9A84C] hover:underline"
              >
                efscreed@gmail.com
              </a>
              . You also have the right to lodge a complaint with the
              Information Commissioner's Office (ICO) at{" "}
              <a
                href="https://ico.org.uk/"
                target="_blank"
                rel="noreferrer"
                className="text-[#C9A84C] hover:underline"
              >
                ico.org.uk
              </a>
              .
            </p>
          </section>

          <section className="space-y-4 pt-4 border-t border-stone-800/80">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              7. Cookies
            </h2>
            <p>
              Our website uses essential functional cookies and standard
              analytics to ensure proper site display and performance. We do not
              use intrusive cross-site tracking cookies.
            </p>
          </section>

          <section className="space-y-4 pt-4 border-t border-stone-800/80">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              8. Contact Us
            </h2>
            <p>
              If you have any questions about this Privacy Policy or how we
              handle your personal data, please contact us:
            </p>
            <div className="bg-[#12121D] p-5 rounded-xl border border-stone-800 space-y-2 text-stone-300">
              <p>
                <strong className="text-white">Exeter Flow Screeds Ltd</strong>
              </p>
              <p>
                Email:{" "}
                <a
                  href="mailto:efscreed@gmail.com"
                  className="text-[#C9A84C] hover:underline"
                >
                  efscreed@gmail.com
                </a>
              </p>
              <p>
                Phone:{" "}
                <a
                  href="tel:07765712609"
                  className="text-[#C9A84C] hover:underline"
                >
                  07765 712609
                </a>{" "}
                /{" "}
                <a
                  href="tel:07470861869"
                  className="text-[#C9A84C] hover:underline"
                >
                  07470 861869
                </a>
              </p>
              <p>Address: Turner Cl, Newton Poppleford, Sidmouth EX10 0EN</p>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
